import { CartDiscountUpdateEvent, StandardEvents } from '@shopify/events';

const configNode = document.getElementById('osh-noches-config');
if (configNode) {
  const config = JSON.parse(configNode.textContent);
  const ownCodes = ['OSH130NOCHES', 'OSH195NOCHES', 'OSH130RECURRENTE', 'OSHNOCHESENVIO'];
  const root = window.Shopify.routes.root;
  let syncing = null;
  let checkoutReady = false;
  let activeRequest = false;
  const money = cents => new Intl.NumberFormat('es-ES', { style: 'currency', currency: config.currency }).format(cents / 100);
  async function request(path, data) {
    const response = await fetch(root + path, data ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data) } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const result = await response.json();
    if (!response.ok || result.status >= 400 || result.errors) throw new Error(result.description || result.message || config.error);
    return result;
  }
  const nasalItems = cart => cart.items.filter(item => String(item.variant_id) === String(config.variant));
  const planForQuantity = n => config.plans?.[Math.min(3, Math.max(1, n))];
  const isOwnPlan = id => Object.values(config.plans || {}).some(plan => plan.id && String(plan.id) === String(id));
  function codesFor(cart) {
    const nasal = nasalItems(cart);
    const once = nasal.filter(item => !item.selling_plan_allocation).reduce((n, item) => n + item.quantity, 0);
    const sub = nasal.filter(item => item.selling_plan_allocation && isOwnPlan(item.selling_plan_allocation.selling_plan.id)).reduce((n, item) => n + item.quantity, 0);
    const codes = (cart.discount_codes || []).map(item => item.code).filter(code => !ownCodes.includes(code.toUpperCase()));
    if (once >= 3) codes.push('OSH195NOCHES');
    else if (once >= 2) codes.push('OSH130NOCHES');
    if (sub >= 2 || once >= 2) codes.push('OSHNOCHESENVIO');
    return [...new Set(codes)];
  }
  function sync(cart, force = false) {
    if (syncing) return syncing;
    syncing = (async () => {
      cart = cart || await request('cart.js');
      const subscriptionLines = nasalItems(cart).filter(item => item.selling_plan_allocation && isOwnPlan(item.selling_plan_allocation.selling_plan.id));
      if (subscriptionLines.length === 1) {
        const line = subscriptionLines[0];
        const selectedPlan = planForQuantity(line.quantity);
        if (selectedPlan?.id && String(line.selling_plan_allocation.selling_plan.id) !== String(selectedPlan.id)) {
          cart = await request('cart/change.js', { line: cart.items.findIndex(item => item.key === line.key) + 1, quantity: line.quantity, selling_plan: selectedPlan.id });
          force = true;
        }
      }
      const desired = codesFor(cart);
      const current = (cart.discount_codes || []).map(item => item.code.toUpperCase()).sort().join(',');
      if (!force && desired.map(code => code.toUpperCase()).sort().join(',') === current) return cart;
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
      const deferred = CartDiscountUpdateEvent.createPromise();
      document.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: desired.map(code => ({ code })), promise: deferred.promise }));
      try {
        const updated = await request('cart/update.js', { discount: desired.join(','), ...(sections.length ? { sections, sections_url: location.pathname } : {}) });
        deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(updated), detail: { sections: updated.sections } });
        return updated;
      } catch (error) { deferred.reject(error); throw error; }
    })().finally(() => { syncing = null; });
    return syncing;
  }
  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    if (!activeRequest) event.promise?.then(() => sync()).catch(() => {});
  });
  document.addEventListener('click', async event => {
    const button = event.target.closest('button[name="checkout"],input[name="checkout"],a[href$="/checkout"]');
    if (!button || checkoutReady) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    try { await sync(); checkoutReady = true; button.click(); }
    catch (error) { const message = document.getElementById('osh-cart-error'); if (message) { message.hidden = false; message.textContent = error.message; } }
    finally { button.removeAttribute('aria-busy'); }
  }, true);
  if (location.pathname.replace(/\/$/, '').endsWith('/cart')) sync().catch(() => {});
  const offer = document.getElementById('osh-offer');
  if (offer) {
    let quantity = 2;
    let subscription = false;
    const button = offer.querySelector('#osh-buy');
    const notice = offer.querySelector('#osh-notice');
    function render() {
      const oncePrices = { 1: 1499, 2: 2499, 3: 3599 };
      const selectedPlan = planForQuantity(quantity);
      const frequency = selectedPlan?.frequency || '';
      const subscriptionTotal = (selectedPlan?.unitPrice || 0) * quantity;
      const total = subscription ? subscriptionTotal : oncePrices[quantity];
      offer.querySelector('#osh-mode').hidden = !selectedPlan?.id;
      offer.querySelector('#osh-renew').hidden = !subscription;
      offer.querySelector('#osh-mode-title')?.replaceChildren(config.modeTitle.replace('[packs]', quantity));
      offer.querySelector('#osh-once-price')?.replaceChildren(money(oncePrices[quantity]));
      offer.querySelector('#osh-sub-price')?.replaceChildren(config.deliveryPrice.replace('[price]', money(subscriptionTotal)));
      offer.querySelector('#osh-sub-description')?.replaceChildren(config.subDescription.replace('[packs]', quantity === 1 ? '1 pack' : `${quantity} packs`).replace('[shipping]', quantity === 1 ? config.singleSubShipping : config.freeSubShipping).replace('[frequency]', frequency));
      offer.querySelector('#osh-renew').textContent = config.renewal.replaceAll('[price]', money(subscriptionTotal)).replace('[shipping]', quantity === 1 ? config.singleSubShipping : config.freeSubShipping).replace('[frequency]', frequency);
      offer.querySelector('#osh-summary').textContent = quantity === 1 ? config.summarySingle : config.summary.replace('[packs]', quantity).replace('[nights]', quantity * 65);
      offer.querySelector('#osh-shipping').textContent = subscription ? (quantity === 1 ? config.recurringSingle : config.recurring).replace('[frequency]', frequency) : quantity === 1 ? config.oneShipping : config.freeShipping;
      offer.querySelector('#osh-total').textContent = money(total);
      button.textContent = subscription ? config.subscribe.replace('[price]', money(total)).replace('[frequency]', frequency) : config.add.replace('[nights]', quantity * 65).replace('[price]', money(total));
    }
    offer.addEventListener('change', event => {
      if (event.target.name === 'osh-pack') quantity = Number(event.target.value);
      if (event.target.name === 'osh-mode') subscription = event.target.value === 'subscription';
      notice.hidden = true;
      render();
    });
    button.addEventListener('click', async () => {
      if (activeRequest) return;
      activeRequest = true; button.disabled = true; button.setAttribute('aria-busy', 'true'); notice.hidden = true;
      let added = false;
      try {
        let cart = await request('cart.js');
        const nasal = nasalItems(cart);
        if (nasal.some(item => Boolean(item.selling_plan_allocation) !== subscription)) throw new Error(config.mixed);
        const existingSubscription = nasal.find(item => item.selling_plan_allocation);
        if (subscription && existingSubscription && (existingSubscription.quantity !== quantity || String(existingSubscription.selling_plan_allocation.selling_plan.id) !== String(planForQuantity(quantity)?.id))) {
          cart = await request('cart/change.js', { line: cart.items.findIndex(item => item.key === existingSubscription.key) + 1, quantity, selling_plan: planForQuantity(quantity).id });
        }
        if (!subscription || !existingSubscription) {
          await request('cart/add.js', { items: [{ id: config.variant, quantity, ...(subscription ? { selling_plan: planForQuantity(quantity).id } : {}) }] });
          added = true;
          cart = await request('cart.js');
        }
        await sync(cart, true);
        window.location.assign(root + 'cart');
      } catch (error) {
        notice.hidden = false;
        notice.textContent = added ? config.addedError : error.message;
        if (added) { const link = document.createElement('a'); link.href = root + 'cart'; link.textContent = config.viewCart; notice.append(' ', link); button.hidden = true; }
      } finally { activeRequest = false; button.disabled = false; button.removeAttribute('aria-busy'); }
    });
    render();
  }
}


